import { apiClient } from "@/lib/api-client";

/** GET /connections/status channel row status (API draft §2). */
export type ConnectionsChannelStatus = "connected" | "disconnected" | "error" | "skipped";

export interface EmailConnectionStatusBlock {
  status?: ConnectionsChannelStatus;
  address?: string | null;
  defaultStudioId?: string | null;
  reconnectRequired?: boolean;
  connectedAt?: string | null;
  provider?: string | null;
}

export interface InstagramConnectionStatusBlock {
  status?: ConnectionsChannelStatus;
  handle?: string | null;
  errorMessage?: string;
  connectedAt?: string | null;
  /** True when BE stored configuration.webhookSubscriptionPending (UX-55). Absent/false = banner dark. */
  webhookSubscriptionPending?: boolean;
}

export interface WhatsappConnectionStatusBlock {
  status?: ConnectionsChannelStatus;
  phoneNumber?: string | null;
  errorMessage?: string;
  connectedAt?: string | null;
  activationStatus?: string | null;
  metaPhoneStatus?: string | null;
}

export interface BookingPlatformConnectionStatusBlock {
  platform?: string | null;
  platformName?: string | null;
  status?: ConnectionsChannelStatus | string;
  errorMessage?: string;
  lastSyncedAt?: string;
  connectedAt?: string | null;
}

/** GET /api/v1/connections/status success payload (`data`). */
export interface ConnectionsStatusPayload {
  channelMode?: "mock" | "hybrid" | "live" | null;
  instagram?: InstagramConnectionStatusBlock;
  whatsapp?: WhatsappConnectionStatusBlock;
  email?: EmailConnectionStatusBlock;
  bookingPlatform?: BookingPlatformConnectionStatusBlock;
}

/** Tenant-wide read scope for GET /connections/status (email + §2.7). */
export const TENANT_CONNECTIONS_SCOPE = "all" as const;

export function fetchConnectionsStatus(studioId: string) {
  return apiClient<ConnectionsStatusPayload>("/connections/status", { studioId });
}

export function startEmailConnect(params: {
  returnPath: string;
  defaultStudioId?: string;
  studioId?: string;
}) {
  const query = new URLSearchParams({ returnPath: params.returnPath });
  if (params.defaultStudioId) {
    query.set("defaultStudioId", params.defaultStudioId);
  }
  return apiClient<{ authorizeUrl?: string }>(`/connections/email/authorize?${query.toString()}`, {
    studioId: params.studioId ?? TENANT_CONNECTIONS_SCOPE,
  });
}

export function disconnectEmailConnection() {
  return apiClient<void>("/connections/email/disconnect", {
    method: "POST",
    studioId: TENANT_CONNECTIONS_SCOPE,
    idempotent: true,
  });
}
